import React, { lazy, Suspense, useEffect } from 'react';
import './App.css';
import './hbk.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ui/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import Home from './pages/Home';
import { RouteMetadata } from './seo/RouteMetadata';

const IntakeRedirect = lazy(() => import('./components/IntakeRedirect'));
const ConnectedCarePage = lazy(() => import('./components/ConnectedCarePage'));
const ExplainResults = lazy(() => import('./components/ExplainResults'));
const ResearchPage = lazy(() => import('./components/ResearchPage'));
const YaraPage = lazy(() => import('./components/YaraPage'));
const OrderPage = lazy(() => import('./components/OrderPage'));
const PanelDetailPage = lazy(() => import('./components/PanelDetailPage'));
const ConnectPage = lazy(() => import('./components/ConnectPage'));
const LoginPage = lazy(() => import('./components/LoginPage'));
const AccountPage = lazy(() => import('./components/AccountPage'));
const AuthCallback = lazy(() => import('./components/AuthCallback'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./components/TermsOfService'));
const NotFound = lazy(() => import('./components/NotFound'));

const RouteScroll = () => {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) { window.scrollTo({ top: 0, behavior: 'instant' }); return undefined; }
    const scroll = () => document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: 'instant', block: 'start' });
    scroll();
    const timer = setTimeout(scroll, 250);
    return () => clearTimeout(timer);
  }, [pathname, hash]);
  return null;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) return <AuthCallback />;
  return <><RouteMetadata /><RouteScroll /><Routes>
    <Route path="/" element={<Home />} />
    <Route path="/order" element={<OrderPage />} />
    <Route path="/tests/:panelId" element={<PanelDetailPage />} />
    <Route path="/results" element={<ExplainResults />} />
    <Route path="/yara" element={<YaraPage />} />
    <Route path="/connected-care" element={<ConnectedCarePage />} />
    <Route path="/intake" element={<IntakeRedirect />} />
    <Route path="/research" element={<ProtectedRoute title="Sign in to use Research." description="The cited-research tool is reserved for Integrated Wellness coaches and team members. Sign in with your Google account to continue."><ResearchPage /></ProtectedRoute>} />
    <Route path="/connect" element={<ConnectPage />} />
    <Route path="/rhea" element={<Navigate to="/yara" replace />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/account" element={<ProtectedRoute title="Sign in to manage your account." description="Staff only. Sign in to change your password."><AccountPage /></ProtectedRoute>} />
    <Route path="/privacy" element={<PrivacyPolicy />} />
    <Route path="/terms" element={<TermsOfService />} />
    <Route path="*" element={<NotFound />} />
  </Routes></>;
};

export default function App() {
  return <ErrorBoundary><div className="App"><BrowserRouter><AuthProvider><Suspense fallback={<p className="hbk-route-loading" role="status" data-testid="route-loading">Loading HomeBloodKit…</p>}><AppRouter /></Suspense></AuthProvider></BrowserRouter></div></ErrorBoundary>;
}